import {ArticleInfoModel} from '@components/ArticleInfo/ArticleInfoModel';
import {
    FilterRangeModel,
    FiltersModel,
    SearchModel,
    SearchRequest,
    SectionItemModel
} from '../interfaces';
import {createProposal} from './createProposal';
import {ISelectOption, SharedRangeDatePickerValue} from '@frontend/shared';

function articlesAreEqual(proposalArticle: string, requestArticle: string) {
    let result = proposalArticle === requestArticle;
    if (!result) {
        if (proposalArticle[0] === '0' || requestArticle[0] === '0') {
            const trimmedProposalArticle = proposalArticle.replace(/^0+/, '');
            const trimmedRequestArticle = requestArticle.replace(/^0+/, '');
            result = trimmedProposalArticle === trimmedRequestArticle;
        }
    }
    return result;
}

function checkIfBrandsAreEqual(proposalBrand: string, requestBrand: string) {
    return proposalBrand.toLowerCase() === requestBrand.toLowerCase();
}

export function processSearchApiResults({
    searchResults,
    searchRequest
}: {
    searchResults: any[];
    searchRequest: SearchRequest;
}): [SearchModel, FiltersModel] {
    const searchedItems = new Map<string, SectionItemModel>();
    const originalItems = new Map<string, SectionItemModel>();
    const analogItems = new Map<string, SectionItemModel>();

    const sourceSet = new Set<string>();
    const supplierSet = new Set<string>();

    const ratingRange: FilterRangeModel = {};
    const quantityRange: FilterRangeModel = {};
    const multiplicityRange: FilterRangeModel = {};
    const deliveryDaysRange: FilterRangeModel = {};
    const deliveryDateRange: SharedRangeDatePickerValue = {start: null, end: null};
    let isReturnPossible = false;
    let isOfficialDealer = false;

    for (let generation = 0; generation < searchResults.length; generation++) {
        const genX = searchResults[generation];
        genX.forEach((x: any) => {
            const header = {
                article: x.requestedArticle,
                articleName: x.articleName,
                brand: x.brand,
                brandName: x.brandName
            } as ArticleInfoModel;

            const proposal = createProposal(x, generation);

            const brandsAreEqual = checkIfBrandsAreEqual(x.brandName, searchRequest.brand);
            let currentCollection: Map<string, SectionItemModel>;
            if (brandsAreEqual && articlesAreEqual(x.requestedArticle, searchRequest.article)) {
                currentCollection = searchedItems;
            } else if (brandsAreEqual) {
                currentCollection = originalItems;
            } else {
                currentCollection = analogItems;
            }

            let itemKey: string;
            if (currentCollection === analogItems) {
                const normalizedArticle = x.requestedArticle.replace(/^0+/, '') || '0';
                itemKey = x.brandName + '::' + normalizedArticle;
            } else {
                itemKey = x.brandName + '::' + x.requestedArticle;
            }

            const item = currentCollection.get(itemKey);
            if (item) {
                item.proposals.push(proposal);

                if (currentCollection === analogItems) {
                    const currentArticle = item.header.article;
                    const newArticle = x.requestedArticle;
                    const requestArticle = searchRequest.article;

                    const currentMatchesRequest = articlesAreEqual(currentArticle, requestArticle);
                    const newMatchesRequest = articlesAreEqual(newArticle, requestArticle);

                    if (newMatchesRequest && !currentMatchesRequest) {
                        // Новый артикул совпадает с запросом, а текущий - нет
                        item.header = header;
                    } else if (!newMatchesRequest && !currentMatchesRequest) {
                        // Оба не совпадают с запросом - выбираем более длинный
                        if (newArticle.length > currentArticle.length) {
                            item.header = header;
                        }
                    }
                }
            } else {
                currentCollection.set(itemKey, {key: itemKey, header, proposals: [proposal]});
            }

            if (x.source) {
                sourceSet.add(x.source);
            }
            if (x.webSupplierName) {
                supplierSet.add(x.webSupplierName);
            }
            if (x.supplierName) {
                supplierSet.add(x.supplierName);
            }

            if (x.isReturnPossible) {
                isReturnPossible = true;
            }
            if (x.isOfficialDealer) {
                isOfficialDealer = true;
            }
        });
    }

    const defaultSources = ['лп', 'вп'];
    defaultSources.forEach((s) => sourceSet.add(s));

    const labelMap: Record<string, string> = {
        лп: 'Локальный',
        вп: 'Веб'
    };

    const sourceOptions: ISelectOption[] = Array.from(sourceSet).map((s) => ({
        label: labelMap[s] ?? s,
        value: s,
        isSelected: false
    }));
    const supplierOptions: ISelectOption[] = Array.from(supplierSet).map((s) => ({
        label: s,
        value: s,
        isSelected: false
    }));

    const searchModel: SearchModel = {
        searched: Array.from(searchedItems.values()),
        original: Array.from(originalItems.values()),
        analogs: Array.from(analogItems.values())
    };

    const filtersModel: FiltersModel = {
        source: sourceOptions,
        supplier: supplierOptions,
        ratingRange,
        quantityRange,
        multiplicityRange,
        deliveryDaysRange,
        deliveryDateRange,
        isReturnPossible,
        isOfficialDealer
    };

    return [searchModel, filtersModel];
}
